import get from 'lodash.get';
import queryString from 'qs';
import isEqual from 'react-fast-compare';

import { GuidService } from 'ui-components/services/guid-service';

import * as Actions from './orders.grid.actions';
import * as OrderHeaderActions from './orders.grid.header.actions';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { getParsedIntValueOrDefault } from '../../../hooks/use-user-setting';
import { GridConfigStateService } from '../../../services/grid-config-state-service';
import { GridConfigUserSettingService } from '../../../services/grid-config-user-setting-service';
import { calculatePagingStats, getFilteredPagingStats } from '../../../services/pagination-service';
import * as AlertDetailActions from '../../../store/alerts/details/alerts.details.actions';
import * as OrdersNavAction from '../../../store/orders/nav/orders.nav.actions';
import * as OrdersActions from '../../../store/orders/orders.actions';
import * as SellerActions from '../../../store/seller/seller.actions';
import { APP_INITIALIZATION } from '../../core/base-actions';
import { ENTITIES_LOAD, ENTITIES_PATCH_ALTERNATIVE_RATES } from '../../entities/entities.actions';
import { SET_HOLD_UNTIL_DATE_REQUEST } from '../orders.actions';

// TODO: figure out and extract default paging to some where central
const query = queryString.parse(window.location.search);
const filteredQuery = window.location.pathname.includes('orders') ? query : emptyObject;
const initialPagingStats = calculatePagingStats(filteredQuery.page, filteredQuery.pageSize);

const initialState = {
  customViewColumnConfiguration: undefined,
  userViewConfigurations: emptyObject,
  orders: emptyArray,
  ...initialPagingStats,
  isLoading: false,
  isGroupBysCountLoading: false,
  isSidebarVisible: true,
  rateIsProcessing: false,
  rateJobs: emptyArray,
  alternativeRateJobs: emptyArray,
  expandedRows: {},
  activeGroupBys: [],
  groupByCount: [],
  hasGetOrdersFailure: false,
};

const getNewStateFromPagingData = (state, newPage, pageSize) => {
  return {
    ...state,
    isLoading: true,
    ...getFilteredPagingStats(newPage, pageSize, state.totalRecords, state.pageSize),
    expandedRows: {},
  };
};

const getOrdersSuccess = (state, action) => {
  const { orders, page, pageSize, totalCount } = action;
  const pagingStats = calculatePagingStats(page, pageSize, totalCount);

  let newOrders = orders;

  if (orders && Array.isArray(orders)) {
    newOrders = orders.map((order, index) => {
      if (isEqual(order, state.orders[index])) {
        return state.orders[index];
      }

      return order;
    });
  }

  return {
    ...state,
    orders: newOrders,
    ...pagingStats,
    isLoading: false,
  };
};

const getUserSettingsState = (state, action) => {
  const { data } = action;
  const userSettings = get(data, 'userSettings', emptyArray);
  const stores = get(data, 'stores', emptyArray);
  const stateDrawerVisiblity = get(state, 'isSidebarVisible', false);
  const storedDrawerVisibilitySetting = userSettings.find((setting) => setting.settingName === 'v3.orders.grid.drawerVisibility');
  const pageSizeUserSetting = userSettings.find((setting) => setting.settingName === UserSettings.ordersGridPageSize.settingName);
  const pageSize = getParsedIntValueOrDefault(pageSizeUserSetting?.settingValue, undefined);

  const storedSettingIsVisible = storedDrawerVisibilitySetting ? storedDrawerVisibilitySetting.settingValue === 'true' : false;
  const mergedAppVersionConfigs = GridConfigUserSettingService.mergeV2AndV3OrdersGridConfigs(userSettings, stores) || {};
  return {
    ...state,
    isSidebarVisible: storedDrawerVisibilitySetting ? storedSettingIsVisible : stateDrawerVisiblity,
    userViewConfigurations: mergedAppVersionConfigs,
    pageSize: !filteredQuery.pageSize && pageSize ? pageSize : state.pageSize,
  };
};

const orderGridRatesRequest = (state, { fulfillmentPlanIds }) => ({
  ...state,
  rateIsProcessing: fulfillmentPlanIds,
});

const orderGridRatesSuccess = (state, { response }) => {
  const rateJobGuids = response?.rateJobGuids || emptyArray;
  const alternativeRateJobGuids = response?.alternativeRateJobGuids || emptyArray;

  const rateJobs = rateJobGuids.map((jobId) => ({ jobId, isDone: false }));
  const alternativeRateJobs = alternativeRateJobGuids.map((jobId) => ({
    jobId,
    isDone: false,
  }));

  return {
    ...state,
    rateJobs,
    alternativeRateJobs,
  };
};

const clearRates = (state) => ({
  ...state,
  rateIsProcessing: false,
  rateJobs: emptyArray,
});

const updateRates = (state, { data }) => {
  const jobId = get(data, 'jobId');
  const isDone = get(data, 'final');
  const currentJobs = get(state, 'rateJobs', emptyArray);

  // Only filter out the completed job, not all jobs
  const rateJobs = isDone ? currentJobs.filter((job) => job.jobId !== jobId) : currentJobs;

  if (rateJobs.length === 0) {
    return clearRates(state);
  }

  return {
    ...state,
    rateJobs,
  };
};

const updateAlternativeRateJobs = (state, { alternativeRatesPayload }) => {
  const currentJobs = state?.alternativeRateJobs || emptyArray;

  if (!currentJobs.length || !alternativeRatesPayload?.final) {
    return state;
  }

  const alternativeRateJobs = currentJobs.filter((job) => job.jobId !== alternativeRatesPayload?.jobId);

  if (alternativeRateJobs.length === 0) {
    return {
      ...state,
      alternativeRateJobs: emptyArray,
    };
  }

  return {
    ...state,
    alternativeRateJobs,
  };
};

export const ordersGrid = (state = initialState, action = {}) => {
  const { newPage, pageSize, isLoading, fulfillmentPlanId, isGroupBysCountLoading } = action;
  switch (action.type) {
    case ENTITIES_LOAD:
      return {
        ...state,
      };
    case Actions.ORDER_GRID_CHANGE_COLUMNS:
    case Actions.ORDER_GRID_CHANGE_ORDERBY: {
      const { statusViewId, filterViewId, columns } = action;
      if (filterViewId === 'custom') {
        return {
          ...state,
          customViewColumnConfiguration: columns,
        };
      }
      return GridConfigStateService.updateStateViewConfig(state, statusViewId, filterViewId, columns);
    }
    case Actions.ORDER_GRID_UPDATE_CUSTOM_CONFIGURATION:
      return {
        ...state,
        customViewColumnConfiguration: action.customViewColumnConfiguration,
      };
    case Actions.ORDER_GRID_CHANGE_PAGE:
      return getNewStateFromPagingData(state, newPage, pageSize);
    case Actions.ORDER_GRID_TOGGLE_SIDEBAR_VISIBILITY:
      return { ...state, isSidebarVisible: !state.isSidebarVisible };
    case Actions.ORDER_GRID_SHOW_SPINNER:
    case OrdersActions.GET_ORDERS_REQUEST:
      return { ...state, isLoading: true };
    case Actions.SET_IS_LOADING:
      return { ...state, isLoading };
    case OrdersActions.GET_ORDERS_SUCCESS:
      return getOrdersSuccess(state, action);
    case OrdersNavAction.ORDERS_CHANGE_VIEW_SEARCH:
      return getNewStateFromPagingData(state, action.setPageToOne ? 1 : state.page);
    case APP_INITIALIZATION:
      return getUserSettingsState(state, { data: action.initialData });
    case SellerActions.GET_SELLER_DATA_SUCCESS:
      return getUserSettingsState(state, action);
    case OrderHeaderActions.ORDER_GRID_GET_RATES_REQUEST:
      return orderGridRatesRequest(state, action);
    case OrderHeaderActions.ORDER_GRID_GET_RATES_SUCCESS:
      return orderGridRatesSuccess(state, action);
    case OrderHeaderActions.ORDER_GRID_RATES_UPDATED:
      return updateRates(state, action);
    case OrderHeaderActions.ORDER_GRID_RATES_CLEAR:
      return clearRates(state);
    case AlertDetailActions.GET_ALERT_DETAILS:
      return {
        ...state,
        orders: emptyArray,
        isLoading: true,
      };
    case AlertDetailActions.GET_ALERT_DETAILS_SUCCESS:
      return {
        ...state,
        orders: get(action, 'alertDetails.orders') || emptyArray,
        isLoading: false,
      };
    case OrdersActions.QUICK_SEARCH_ORDERS_SUCCESS:
      return getOrdersSuccess(state, action);
    case Actions.ORDER_GRID_EXPAND_ROW:
      return {
        ...state,
        expandedRows: {
          ...state.expandedRows,
          [fulfillmentPlanId]: true,
        },
      };
    case Actions.ORDER_GRID_SET_GROUPBYS: {
      const { activeGroupBys } = action;
      const hasActiveGroupBys = !!activeGroupBys.length;
      return {
        ...state,
        activeGroupBys,
        isGroupBysCountLoading: hasActiveGroupBys,
        groupByCount: hasActiveGroupBys ? state.groupByCount : [],
      };
    }
    case Actions.ORDER_GRID_SET_GROUPBYS_COUNT: {
      const { groupByCount } = action;
      return {
        ...state,
        groupByCount: groupByCount.map((gb) => ({
          ...gb,
          groupById: GuidService.newGuid(),
        })),
        isGroupBysCountLoading: false,
      };
    }
    case Actions.SET_ORDER_GRID_IS_GROUPBYS_COUNT_LOADING: {
      return { ...state, isGroupBysCountLoading };
    }
    case SET_HOLD_UNTIL_DATE_REQUEST: {
      const { payload } = action;

      return {
        ...state,
        orders: state.orders.filter((fpId) => !payload.fulfillmentPlanIds.includes(fpId)),
      };
    }
    case OrdersActions.GET_ORDERS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        hasGetOrdersFailure: true,
      };
    }
    case OrdersActions.CLEAR_GET_ORDERS_FAILURE: {
      return {
        ...state,
        hasGetOrdersFailure: false,
      };
    }
    case ENTITIES_PATCH_ALTERNATIVE_RATES: {
      return updateAlternativeRateJobs(state, action);
    }
    default:
      return state;
  }
};

export { namespace } from './orders.grid.reducer.metadata';
